import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Notistack
import { useSnackbar } from 'notistack';

// MUI
import { Grid, Divider, Fab, Dialog, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { Edit as SignIcon } from '@mui/icons-material';

// Apollo
import { useMutation } from '@apollo/client';
import { UPLOAD_REFLECTION_PHASE_LEARNER_SIGNATURE } from './gql-upload-learner-signature';

// Services
import { IReflectionPhase } from 'services/interfaces';
import { getErrorExtensions } from 'services/utils';

// Components
import Title from 'components/title';
import SignCard from 'components/sign-card';
import PelletCard from 'components/pellet-card';
import { isCanvasBlank } from 'components/sign-card/utils';

// ---------------------------------------------------------------------------------------------------- \\
// ------------------------------------- UPLOAD LEARNER SIGNATURE ------------------------------------- \\
// ---------------------------------------------------------------------------------------------------- \\

const UploadLearnerSignature = ({
    reflectionPhase,
    refetch,
} : {
    reflectionPhase: IReflectionPhase | undefined,
    refetch: any,
}) => {

    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <>
            <PelletCard
                paddingTop={0}
                startIcon={<SignIcon color="success" />}
                summary={(
                    <Grid item xs>
                        {t('learner_signature')}
                    </Grid>
                )}
            >
                <table>
                    <thead>
                        <tr>
                            <td className="table-td">
                                <Title>
                                    {t('name')}
                                </Title>
                            </td>
                            <td className="table-td">
                                <Title>
                                    {t('status')}
                                </Title>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="table-td">
                                {reflectionPhase?.learnerWorkSituation?.learner?.fullName}
                            </td>
                            <td className="table-td">
                                {reflectionPhase?.learnerSignatureUrl ? t('signed') : t('waiting_signature')}
                            </td>
                            <td className="table-td">
                                <Fab
                                    onClick={() => setOpen(true)}
                                    color="success"
                                >
                                    <SignIcon />
                                </Fab>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </PelletCard>
            {open && (
                <SignatureDialog
                    reflectionPhase={reflectionPhase}
                    onClose={() => setOpen(false)}
                    refetch={() => { setOpen(false); refetch(); }}
                />
            )}
        </>
    );
};

// ---------------------------------------------------------------------------------------------------- \\
// ----------------------------------------- SIGNATURE DIALOG ----------------------------------------- \\
// ---------------------------------------------------------------------------------------------------- \\

const SignatureDialog = ({
    reflectionPhase,
    onClose,
    refetch,
} : {
    reflectionPhase: IReflectionPhase | undefined,
    onClose: () => void,
    refetch: any,
}) => {

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [uploadLearnerSignature, { loading, error, data }] = useMutation(UPLOAD_REFLECTION_PHASE_LEARNER_SIGNATURE);

    const onUploadLearnerSignature = () => {
        if (isCanvasBlank()) {
            enqueueSnackbar(t('missing_signature'), { variant: 'warning' });
        }
        else {
            let canvas = document.getElementById('signCanvas') as HTMLCanvasElement;
            canvas.toBlob(blob => {
                uploadLearnerSignature({
                    variables: {
                        id: reflectionPhase?.id,
                        file: blob,
                    },
                });
            });
        }
    };

    useEffect(() => {
        if (data?.uploadReflectionPhaseLearnerSignature) {
            enqueueSnackbar(t('added_successfully'), { variant: 'success' });
            refetch();
        }
        if (error) {
            enqueueSnackbar(getErrorExtensions(error), { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, error]);

    return (
        <Dialog
            id="dialog"
            open
            onClose={onClose}
            fullScreen
        >
            <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="dialog-title">
                    {t('signature')} {t('of.1')} {reflectionPhase?.learnerWorkSituation?.learner?.fullName}
                </div>
                <SignCard />
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button
                    onClick={onClose}
                    color="inherit"
                >
                    {t('close')}
                </Button>
                <Button
                    onClick={onUploadLearnerSignature}
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <></>}
                    color="success"
                >
                    {t('validate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadLearnerSignature;
